import { type Ref, useMemo, useRef } from 'react';
import { createCalendar } from '@internationalized/date';
import { type DateFieldStateOptions, type DateValue, useDateFieldState } from '@react-stately/datepicker';
import { clsx } from 'clsx';
import { useDateField } from 'react-aria';
import { match } from 'ts-pattern';
import { Segment } from '../segment/segment';
import styles from './date-field.module.scss';

interface DateFieldProps extends Partial<DateFieldStateOptions> {
  errorId: string | undefined;
  labelId: string | undefined;
  placeholderValue?: DateValue | null | undefined;
  ref?: Ref<HTMLDivElement>;
}

export function DateField({ errorId, labelId, placeholderValue, ref, ...props }: DateFieldProps) {
  const state = useDateFieldState({
    ...props,
    // enforce YYYY-MM-DD format
    locale: 'en-CA',
    createCalendar,
    placeholderValue,
  });

  const isPlaceholder = state.segments.every((segment) => !segment.isEditable || segment.isPlaceholder);

  const baseRef = useRef(null);
  const { fieldProps } = useDateField(props, state, baseRef);

  const segments = useMemo(() => {
    if (isPlaceholder && placeholderValue) {
      return state.segments.map((segment) => {
        if (segment.isEditable) {
          return {
            ...segment,
            placeholder: match(segment.type)
              .with('day', () => String(placeholderValue.day).padStart(2, '0'))
              .with('month', () => String(placeholderValue.month).padStart(2, '0'))
              .with('year', () => String(placeholderValue.year))
              .with('hour', () => {
                if ('hour' in placeholderValue) {
                  // Convert hour to 12-hour format if needed
                  const hour = state.dateFormatter.resolvedOptions().hour12 ? placeholderValue.hour % 12 || 12 : placeholderValue.hour;
                  return String(hour).padStart(2, '0');
                }

                return segment.placeholder;
              })
              .with('minute', () => {
                if ('hour' in placeholderValue) {
                  return String(placeholderValue.minute).padStart(2, '0');
                }

                return segment.placeholder;
              })
              .with('second', () => {
                if ('hour' in placeholderValue) {
                  return String(placeholderValue.second).padStart(2, '0');
                }

                return segment.placeholder;
              })
              .with('dayPeriod', () => {
                if ('hour' in placeholderValue) {
                  const hour = placeholderValue.hour;
                  return hour < 12 ? 'A.M.' : 'P.M.';
                }

                return segment.placeholder;
              })
              .otherwise(() => segment.placeholder),
          };
        }

        return segment;
      });
    }

    return state.segments;
  }, [state.segments, isPlaceholder, placeholderValue, state.dateFormatter]);

  return (
    <div aria-errormessage={errorId} className={clsx(styles.dateField, isPlaceholder && styles.isPlaceholder)} ref={ref} {...fieldProps}>
      {segments.map((segment, i) => (
        <Segment key={`${segment.type}-${i}`} segment={segment} state={state} withModifiedPlaceholder={isPlaceholder && Boolean(placeholderValue)} />
      ))}
    </div>
  );
}
